import React, { useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';
import AgentChat from './components/Agent/AgentChat';
import { useOrganizationData } from './hooks/useOrganizationData';
import { useOrgTheming } from './hooks/useOrgTheming';
import { canAccessFeature } from './utils/tierPermissions';

import './styles/app.scss';
import './components/Agent/AgentChat.scss';

const DEFAULT_COLOR = '#0f3a60';

function AgentWidget({ organizationId }: { organizationId: string }) {
  const { data, loading, error } = useOrganizationData(organizationId);

  const tier = data?.organization?.tier;
  const orgName = data?.organization?.name ?? '';
  const primaryColor = data?.organizationSettings?.primary_color || DEFAULT_COLOR;

  useOrgTheming(primaryColor);

  useEffect(() => {
    document.documentElement.style.setProperty('--agent-primary-color', primaryColor);
  }, [primaryColor]);

  if (loading) return null;
  if (error || !data?.organization) return null;
  if (!canAccessFeature(tier, 'aiAgent')) return null;

  return (
    <AgentChat
      organizationId={organizationId}
      organizationName={orgName}
      primaryColor={primaryColor}
      embedded={true}
    />
  );
}

class OperviaAgentElement extends HTMLElement {
  private root: Root | null = null;
  private mountPoint: HTMLDivElement | null = null;

  static get observedAttributes() {
    return ['org-id'];
  }

  connectedCallback() {
    const orgId = this.getAttribute('org-id');

    if (!orgId) {
      console.error('Opervia Agent Widget: org-id attribute is required');
      return;
    }

    this.mountPoint = document.createElement('div');
    this.mountPoint.className = 'opervia-agent-widget-root';
    this.appendChild(this.mountPoint);

    this.root = createRoot(this.mountPoint);
    this.root.render(
      <React.StrictMode>
        <AgentWidget organizationId={orgId} />
      </React.StrictMode>
    );

    window.dispatchEvent(new CustomEvent('opervia-agent-loaded'));
  }

  disconnectedCallback() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (name === 'org-id' && oldValue !== newValue && this.root && this.mountPoint) {
      this.root.render(
        <React.StrictMode>
          <AgentWidget organizationId={newValue} />
        </React.StrictMode>
      );
    }
  }
}

if (!customElements.get('opervia-agent')) {
  customElements.define('opervia-agent', OperviaAgentElement);
}

export { OperviaAgentElement, AgentWidget };

if (typeof window !== 'undefined') {
  console.log('Opervia Agent Widget loaded successfully');
}
